/**
 * Tenant config for the SPA: the context <TenantConfigProvider> fills from the
 * BFF (/api/storefront/config), and the hook pages read it through.
 */
import type { StorefrontConfig, StorefrontFeatureKey } from "@loot-os/api-contract";
import { createContext, useContext } from "react";

/** What the BFF serves: the wire config without the BFF-only publishable key. */
export type TenantConfig = Omit<StorefrontConfig, "publishableKey">;

/** Resolved config plus the feature check every page gates on. */
export interface TenantContextValue {
  config: TenantConfig;
  hasFeature: (key: StorefrontFeatureKey) => boolean;
  /**
   * The config was fetched on this page load, not only painted from the tab's saved copy. What
   * must never show on a store that has switched it off (the chat) waits for this.
   */
  configFresh: boolean;
}

let bypassConfigCache = false;

/**
 * Makes the next config fetch skip the browser's cache (the BFF allows 30 s): set when the API
 * says a feature the page shows is off, so the refetch sees the switch that was just flipped.
 */
export function bypassNextConfigCache(): void {
  bypassConfigCache = true;
}

/** Whether this fetch must skip the cache; answering clears the request. */
export function takeConfigCacheBypass(): boolean {
  const bypass = bypassConfigCache;
  bypassConfigCache = false;
  return bypass;
}

/** Written by <TenantConfigProvider>; read through useTenant. */
export const TenantContext = createContext<TenantContextValue | null>(null);

/** This store's config; throws outside the provider. */
export function useTenant(): TenantContextValue {
  const value = useContext(TenantContext);
  if (!value) throw new Error("useTenant must be used inside <TenantConfigProvider>");
  return value;
}
